import { Masonry } from "@mui/lab"
import { Box, Typography } from "@mui/material"

import type { FC, FCWC, TripTeaserNewProps } from ".."
import { BlogArticleTeaser, Container, TeaserCard, TripTeaserNew } from ".."
import type { BlogArticleTeaserProps } from "../BlogArticleTeaser/BlogArticleTeaser"
import type { TeaserCardProps } from "../TeaserCard/TeaserCard"
import type { TripTeaserProps } from "../TripTeaser/TripTeaser"

type WishlistItemType = "trip" | "article" | "guide"

type WishlistItemDataMap = {
  trip: TripTeaserProps
  article: BlogArticleTeaserProps
  guide: TeaserCardProps
}

type WishlistSectionProps = {
  title: string
  type: WishlistItemType
  items: WishlistItemDataMap[WishlistItemType][]
  onUnlike?: (arg?: { contentType?: string; nid?: number }) => void
}

const WishlistSection: FC<WishlistSectionProps> = ({ title, type, items, onUnlike }) => {
  return (
    <Container
      maxWidth={1450}
      padding={(theme) => ({ xs: theme.spacing(8, 0, 0), lg: theme.spacing(15, 5, 0) })}
    >
      <WithListSectionHeader title={title} />
      <WishlistCollection type={type}>
        {items?.map((item, i) => (
          <WishlistCard key={i} type={type} data={item} onUnlike={onUnlike} />
        ))}
      </WishlistCollection>
    </Container>
  )
}

export default WishlistSection

/** A sub-component for the wishlist section header */
const WithListSectionHeader: FC<Pick<WishlistSectionProps, "title">> = ({ title }) => {
  return (
    <Box display="flex" gap={2} mb={{ xs: 5, lg: 8 }}>
      <Typography variant="heading.lg" component="h2" fontSize={32} fontWeight={800}>
        {title}
      </Typography>
    </Box>
  )
}

/** A sub-component for the wishlist items wrapper */
const WishlistCollection: FCWC<Pick<WishlistSectionProps, "type">> = ({ type, children }) => {
  switch (type) {
    case "trip":
      return (
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "minmax(0, 1fr)",
            md: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(3, minmax(0, 1fr))",
          }}
          sx={{
            columnGap: { xs: 4, md: 10 },
            rowGap: { xs: 4, md: 10 },
          }}
        >
          {children}
        </Box>
      )
    case "article":
      return (
        <Box m={{ xs: -2, md: -5 }}>
          <Masonry columns={{ xs: 1, md: 2, xl: 3 }}>
            <>{children}</>
          </Masonry>
        </Box>
      )

    case "guide":
      return (
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          }}
          columnGap={{ xs: 3, md: 5 }}
          rowGap={{ xs: 3, md: 5 }}
        >
          {children}
        </Box>
      )

    default:
      return <>{children}</>
  }
}

/** A sub-component for the wishlist card */
type WishlistCardProps = {
  type: WishlistItemType
  data: WishlistItemDataMap[WishlistItemType]
  onUnlike?: (arg?: { contentType?: string; nid?: number }) => void
}
const WishlistCard: FC<WishlistCardProps> = ({ type, data, onUnlike }) => {
  switch (type) {
    case "trip":
      return <TripTeaserNew onRemoveFromWishList={onUnlike} {...(data as TripTeaserNewProps)} />
    case "article":
      return (
        <Box p={{ xs: 2, md: 5 }}>
          <BlogArticleTeaser onUnlike={onUnlike} {...(data as BlogArticleTeaserProps)} />
        </Box>
      )
    case "guide":
      return <TeaserCard {...(data as TeaserCardProps)} />
    default:
      return <></>
  }
}
